import { haversineNm, type LonLat, lonDelta } from './geo.js';

/**
 * Deniz rotası ve mesafesi (ADR 0048 §2): Eurostat SeaRoute "marnet" ağı üzerinde Dijkstra. Sonuç
 * yaklaşıktır, seyir planlaması için değildir. Ağ verisi `@felix/sea-data`'dadır (EUPL-1.2);
 * buradaki kod veriyi parametre olarak alır, I/O yapmaz.
 */

/** Ağdaki adlı geçitler (marnet `pass` özniteliği, searoute v3.5). */
export const SEA_PASSES = [
  'suez',
  'panama',
  'malacca',
  'gibraltar',
  'dover',
  'bering',
  'magellan',
  'babelmandeb',
  'kiel',
  'corinth',
  'northwest',
  'northeast',
] as const;
export type SeaPass = (typeof SEA_PASSES)[number];

/** Varsayılan kaçınılan geçitler: Kuzeybatı ve Kuzeydoğu geçitleri (buz; searoute varsayılanı). */
export const DEFAULT_AVOIDED_PASSES: readonly SeaPass[] = ['northwest', 'northeast'];

/** `@felix/sea-data`'nın ürettiği sıkıştırılmış ağ (build-marnet.mjs). */
export interface EncodedSeaNetwork {
  source: string;
  scale: number;
  passes: readonly string[];
  nodes: string;
  edges: string;
  passEdges: string;
}

export interface SeaNetwork {
  source: string;
  nodeCount: number;
  edgeCount: number;
  lon: Float64Array;
  lat: Float64Array;
  edgeA: Int32Array;
  edgeB: Int32Array;
  edgeLength: Float64Array;
  /** Kenarın geçidi; yoksa null. */
  edgePass: readonly (SeaPass | null)[];
  /** Komşuluk (CSR): `offsets[n]..offsets[n+1]` arası `adjacentEdges`. */
  offsets: Int32Array;
  adjacentEdges: Int32Array;
}

const int36 = (text: string) => {
  const value = Number.parseInt(text, 36);
  if (!Number.isFinite(value)) throw new Error(`INVALID_SEA_NETWORK ${text}`);
  return value;
};

function pairs(text: string): [number, number][] {
  if (text === '') return [];
  return text.split(';').map((pair) => {
    const [a, b] = pair.split(',');
    return [int36(a ?? ''), int36(b ?? '')];
  });
}

/** Sıkıştırılmış ağı çözer; ağ bir kez çözülür, rotalar onu paylaşır. */
export function decodeSeaNetwork(encoded: EncodedSeaNetwork): SeaNetwork {
  const nodePairs = pairs(encoded.nodes);
  const nodeCount = nodePairs.length;
  const lon = new Float64Array(nodeCount);
  const lat = new Float64Array(nodeCount);
  let x = 0;
  let y = 0;
  nodePairs.forEach(([dx, dy], i) => {
    x += dx;
    y += dy;
    lon[i] = x / encoded.scale;
    lat[i] = y / encoded.scale;
  });
  const edgePairs = pairs(encoded.edges);
  const edgeCount = edgePairs.length;
  const edgeA = new Int32Array(edgeCount);
  const edgeB = new Int32Array(edgeCount);
  const edgeLength = new Float64Array(edgeCount);
  const degree = new Int32Array(nodeCount + 1);
  let a = 0;
  edgePairs.forEach(([da, db], i) => {
    a += da;
    const b = a + db;
    if (a < 0 || b < 0 || a >= nodeCount || b >= nodeCount) {
      throw new Error('INVALID_SEA_NETWORK edge');
    }
    edgeA[i] = a;
    edgeB[i] = b;
    edgeLength[i] = haversineNm([lon[a] ?? 0, lat[a] ?? 0], [lon[b] ?? 0, lat[b] ?? 0]);
    degree[a + 1] = (degree[a + 1] ?? 0) + 1;
    degree[b + 1] = (degree[b + 1] ?? 0) + 1;
  });
  const edgePass: (SeaPass | null)[] = new Array(edgeCount).fill(null);
  if (encoded.passEdges !== '') {
    for (const item of encoded.passEdges.split(';')) {
      const [index, pass] = item.split(':');
      const name = encoded.passes[int36(pass ?? '') - 1];
      if (!name || !(SEA_PASSES as readonly string[]).includes(name)) {
        throw new Error(`INVALID_SEA_NETWORK pass ${item}`);
      }
      edgePass[int36(index ?? '')] = name as SeaPass;
    }
  }
  const offsets = new Int32Array(nodeCount + 1);
  for (let n = 0; n < nodeCount; n++) offsets[n + 1] = (offsets[n] ?? 0) + (degree[n + 1] ?? 0);
  const fill = offsets.slice(0, nodeCount);
  const adjacentEdges = new Int32Array(edgeCount * 2);
  for (let e = 0; e < edgeCount; e++) {
    for (const n of [edgeA[e] ?? 0, edgeB[e] ?? 0]) {
      adjacentEdges[fill[n] ?? 0] = e;
      fill[n] = (fill[n] ?? 0) + 1;
    }
  }
  return {
    source: encoded.source,
    nodeCount,
    edgeCount,
    lon,
    lat,
    edgeA,
    edgeB,
    edgeLength,
    edgePass,
    offsets,
    adjacentEdges,
  };
}

export interface SeaRouteOptions {
  /** Kaçınılacak geçitler; verilmezse `DEFAULT_AVOIDED_PASSES`. */
  avoid?: readonly SeaPass[];
}

export interface SeaRouteResult {
  /** Toplam: yaklaşma + deniz, deniz mili. */
  distanceNm: number;
  /** Ağ üzerindeki kısım. */
  seaNm: number;
  /** Noktalardan ağa düz çizgi yaklaşmaların toplamı (ağ dışı kısım; büyükse sonuç zayıftır). */
  approachNm: number;
  /** Rotanın geçtiği adlı geçitler, sırayla. */
  passes: SeaPass[];
}

interface Snap {
  edge: number;
  approachNm: number;
  toA: number;
  toB: number;
}

function node(network: SeaNetwork, n: number): LonLat {
  return [network.lon[n] ?? 0, network.lat[n] ?? 0];
}

/** Noktayı en yakın (kaçınılmayan) kenara izdüşürür (yerel eşdikdörtgen düzlemde). */
function snap(network: SeaNetwork, p: LonLat, avoided: ReadonlySet<SeaPass>): Snap | null {
  const cos = Math.cos((p[1] * Math.PI) / 180);
  let best: { edge: number; t: number; d2: number } | null = null;
  for (let e = 0; e < network.edgeCount; e++) {
    const pass = network.edgePass[e];
    if (pass && avoided.has(pass)) continue;
    const a = node(network, network.edgeA[e] ?? 0);
    const b = node(network, network.edgeB[e] ?? 0);
    const ax = lonDelta(p[0], a[0]) * cos;
    const ay = a[1] - p[1];
    const dx = lonDelta(a[0], b[0]) * cos;
    const dy = b[1] - a[1];
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2));
    const qx = ax + t * dx;
    const qy = ay + t * dy;
    const d2 = qx * qx + qy * qy;
    if (!best || d2 < best.d2) best = { edge: e, t, d2 };
  }
  if (!best) return null;
  const a = node(network, network.edgeA[best.edge] ?? 0);
  const b = node(network, network.edgeB[best.edge] ?? 0);
  let qLon = a[0] + best.t * lonDelta(a[0], b[0]);
  if (qLon > 180) qLon -= 360;
  if (qLon < -180) qLon += 360;
  const point: LonLat = [qLon, a[1] + best.t * (b[1] - a[1])];
  const length = network.edgeLength[best.edge] ?? 0;
  return {
    edge: best.edge,
    approachNm: haversineNm(p, point),
    toA: length * best.t,
    toB: length * (1 - best.t),
  };
}

/** İkili yığın: (mesafe, düğüm). */
class Heap {
  private readonly keys: number[] = [];
  private readonly values: number[] = [];
  get size() {
    return this.keys.length;
  }
  push(key: number, value: number) {
    const keys = this.keys;
    const values = this.values;
    let i = keys.length;
    keys.push(key);
    values.push(value);
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if ((keys[parent] ?? 0) <= key) break;
      keys[i] = keys[parent] ?? 0;
      values[i] = values[parent] ?? 0;
      i = parent;
    }
    keys[i] = key;
    values[i] = value;
  }
  pop(): [number, number] {
    const keys = this.keys;
    const values = this.values;
    const top: [number, number] = [keys[0] ?? 0, values[0] ?? 0];
    const lastKey = keys.pop() ?? 0;
    const lastValue = values.pop() ?? 0;
    const n = keys.length;
    if (n > 0) {
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        if (l >= n) break;
        const r = l + 1;
        const c = r < n && (keys[r] ?? 0) < (keys[l] ?? 0) ? r : l;
        if ((keys[c] ?? 0) >= lastKey) break;
        keys[i] = keys[c] ?? 0;
        values[i] = values[c] ?? 0;
        i = c;
      }
      keys[i] = lastKey;
      values[i] = lastValue;
    }
    return top;
  }
}

interface Search {
  dist: Float64Array;
  /** Düğüme gelinen kenar; başlangıçta −1. */
  via: Int32Array;
}

/** Yanaşılan kenarın iki ucundan başlayan Dijkstra; `stop` verilirse hepsi kesinleşince durur. */
function dijkstra(
  network: SeaNetwork,
  from: Snap,
  avoided: ReadonlySet<SeaPass>,
  stop?: readonly number[],
): Search {
  const dist = new Float64Array(network.nodeCount).fill(Number.POSITIVE_INFINITY);
  const via = new Int32Array(network.nodeCount).fill(-1);
  const done = new Uint8Array(network.nodeCount);
  const heap = new Heap();
  const a = network.edgeA[from.edge] ?? 0;
  const b = network.edgeB[from.edge] ?? 0;
  dist[a] = from.toA;
  dist[b] = Math.min(dist[b] ?? Number.POSITIVE_INFINITY, from.toB);
  heap.push(dist[a] ?? 0, a);
  heap.push(dist[b] ?? 0, b);
  const remaining = stop ? new Set(stop) : null;
  while (heap.size > 0) {
    const [d, n] = heap.pop();
    if (done[n]) continue;
    done[n] = 1;
    if (remaining) {
      remaining.delete(n);
      if (remaining.size === 0) break;
    }
    const end = network.offsets[n + 1] ?? 0;
    for (let k = network.offsets[n] ?? 0; k < end; k++) {
      const e = network.adjacentEdges[k] ?? 0;
      const pass = network.edgePass[e];
      if (pass && avoided.has(pass)) continue;
      const other = network.edgeA[e] === n ? (network.edgeB[e] ?? 0) : (network.edgeA[e] ?? 0);
      const next = d + (network.edgeLength[e] ?? 0);
      if (next < (dist[other] ?? Number.POSITIVE_INFINITY)) {
        dist[other] = next;
        via[other] = e;
        heap.push(next, other);
      }
    }
  }
  return { dist, via };
}

/** Kaynaktan hedef kenara: ağ mesafesi ve son düğüm (aynı kenarda doğrudansa −1). */
function arrive(search: Search, network: SeaNetwork, from: Snap, to: Snap) {
  const a = network.edgeA[to.edge] ?? 0;
  const b = network.edgeB[to.edge] ?? 0;
  const viaA = (search.dist[a] ?? Number.POSITIVE_INFINITY) + to.toA;
  const viaB = (search.dist[b] ?? Number.POSITIVE_INFINITY) + to.toB;
  let best = viaA <= viaB ? { sea: viaA, end: a } : { sea: viaB, end: b };
  if (from.edge === to.edge) {
    const direct = Math.abs(from.toA - to.toA);
    if (direct <= best.sea) best = { sea: direct, end: -1 };
  }
  return Number.isFinite(best.sea) ? best : null;
}

/** Yolun geçtiği geçitler: son düğümden geriye, kenar kenar. */
function passesOf(search: Search, network: SeaNetwork, end: number): SeaPass[] {
  const found: SeaPass[] = [];
  let n = end;
  let guard = network.nodeCount;
  while (n >= 0 && guard-- > 0) {
    const e = search.via[n] ?? -1;
    if (e < 0) break;
    const pass = network.edgePass[e];
    if (pass && found[0] !== pass) found.unshift(pass);
    n = network.edgeA[e] === n ? (network.edgeB[e] ?? -1) : (network.edgeA[e] ?? -1);
  }
  return found;
}

function avoidedSet(options: SeaRouteOptions) {
  return new Set(options.avoid ?? DEFAULT_AVOIDED_PASSES);
}

/** Geçit listesine, tekrar etmeden ekler. */
function withPass(list: SeaPass[], pass: SeaPass | null | undefined, atStart: boolean) {
  if (!pass) return list;
  if (atStart) return list[0] === pass ? list : [pass, ...list];
  return list.at(-1) === pass ? list : [...list, pass];
}

/** İki nokta arasındaki deniz rotasının mesafesi; ağda bağlantı yoksa null. */
export function seaRoute(
  network: SeaNetwork,
  from: LonLat,
  to: LonLat,
  options: SeaRouteOptions = {},
): SeaRouteResult | null {
  if (from[0] === to[0] && from[1] === to[1]) {
    return { distanceNm: 0, seaNm: 0, approachNm: 0, passes: [] };
  }
  const avoided = avoidedSet(options);
  const start = snap(network, from, avoided);
  const goal = snap(network, to, avoided);
  if (!start || !goal) return null;
  const search = dijkstra(network, start, avoided, [
    network.edgeA[goal.edge] ?? 0,
    network.edgeB[goal.edge] ?? 0,
  ]);
  const best = arrive(search, network, start, goal);
  if (!best) return null;
  let passes: SeaPass[] = [];
  if (best.end < 0) {
    passes = withPass(passes, network.edgePass[start.edge], true);
  } else {
    passes = passesOf(search, network, best.end);
    passes = withPass(passes, network.edgePass[start.edge], true);
    passes = withPass(passes, network.edgePass[goal.edge], false);
  }
  const approachNm = start.approachNm + goal.approachNm;
  return { distanceNm: approachNm + best.sea, seaNm: best.sea, approachNm, passes };
}

/**
 * Noktaların ikişer mesafeleri (nokta başına bir tam Dijkstra); bağlantı yoksa null. Liman sırası
 * önerisinin girdisi (ADR 0048 §5).
 */
export function seaDistanceMatrix(
  network: SeaNetwork,
  points: readonly LonLat[],
  options: SeaRouteOptions = {},
): (number | null)[][] {
  const avoided = avoidedSet(options);
  const snaps = points.map((p) => snap(network, p, avoided));
  return points.map((p, i) => {
    const start = snaps[i];
    const search = start ? dijkstra(network, start, avoided) : null;
    return points.map((q, j) => {
      if (i === j || (p[0] === q[0] && p[1] === q[1])) return 0;
      const goal = snaps[j];
      if (!start || !goal || !search) return null;
      const best = arrive(search, network, start, goal);
      return best ? start.approachNm + goal.approachNm + best.sea : null;
    });
  });
}
